import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import FlyoutHeader from 'components/common/omnia/flyout-header-component';
import FlyoutFooter from 'components/common/omnia/flyout-footer-component';
import FlyoutPanelContent from 'components/common/omnia/flyout-panel-content-component';
import ContentViewModes from 'models/data/content-view-modes-model';

const FLYOUT_BLOCK = 'flyoutPanel';
const SCROLLBAR_HEIGHT = 12;
const ANIMATION_DURATION = 500; 

class Flyout extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      shouldRender: props.show
    };
    this.exitTimer = null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show === this.props.show) {
      return;
    }
    if (this.exitTimer) {
      clearTimeout(this.exitTimer);
      this.exitTimer = null;
    }

    if (this.props.show) {
      // Show immediately
      this.setState({ shouldRender: true });
    } else {
      // Hide after animation duration
      this.exitTimer = setTimeout(() => {
        this.setState({ shouldRender: false });
        this.exitTimer = null;
      }, ANIMATION_DURATION);
    }
  }

  componentWillUnmount() {
    if (this.exitTimer) {
      clearTimeout(this.exitTimer);
      this.exitTimer = null;
    }
  }

  render() {
    const {
      children,
      className = 'panelContent',
      position = 'right',
      show,
      contentViewMode,
      flyoutRef,
    } = this.props;
    const { shouldRender } = this.state;
    
    const bottomOffset = contentViewMode?.contentFullMode ? SCROLLBAR_HEIGHT : 0;
    
    const safePosition = ['left', 'right'].includes(position) ? position : 'right';

    if (!shouldRender) {
      return null;
    }

    const animationClass = show ? 'flyoutPanel-enter-done' : 'flyoutPanel-exit';
    
    return (
      <div
        className={classNames(FLYOUT_BLOCK, className, safePosition, animationClass)}
        style={{ bottom: bottomOffset }}
        ref={flyoutRef}
      >
        {children}
      </div>
    );
  }
}

Flyout.propTypes = {
  /** CSS class name to apply to the flyout container. */
  className: PropTypes.string,

  /** Position of the flyout panel - either 'left' or 'right' side of the screen. */
  position: PropTypes.oneOf(['left', 'right']),

  /** Whether the flyout should be visible or hidden. */
  show: PropTypes.bool.isRequired,

  /** Content view mode instance that determines flyout layout and positioning. */
  contentViewMode: PropTypes.instanceOf(ContentViewModes).isRequired,

  /** Reference to the flyout DOM element for external manipulation. */
  flyoutRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),

  /** Child components to render inside the flyout panel. */
  children: PropTypes.node,
};

const mapStateToProps = ({
  ui: {
    statementPage: {
      modes: { contentViewMode },
    },
  },
}) => ({
  contentViewMode,
});

export default connect(mapStateToProps, null)(Flyout);

export { Flyout, FlyoutPanelContent, FlyoutHeader, FlyoutFooter };
